'use client';

import Link from 'next/link';
import type { Item } from '@/lib/types';
import { itemUrl } from '@/lib/item-url';
import WikiImage from './WikiImage';

const CARD_TYPE_STYLES: Record<string, string> = {
  attack: 'bg-red-900/50 text-red-300 border-red-700',
  skill: 'bg-sky-900/50 text-sky-300 border-sky-700',
  power: 'bg-amber-900/50 text-amber-300 border-amber-700',
  curse: 'bg-purple-900/50 text-purple-300 border-purple-700',
  status: 'bg-gray-900/50 text-gray-300 border-gray-700',
  relic: 'bg-violet-900/50 text-violet-300 border-violet-700',
};

const RARITY_STYLES: Record<string, string> = {
  basic: 'text-gray-400',
  starter: 'text-gray-400',
  common: 'text-gray-300',
  uncommon: 'text-green-400',
  rare: 'text-yellow-400',
  boss: 'text-red-400',
  shop: 'text-cyan-400',
  event: 'text-blue-400',
  special: 'text-pink-400',
};

function formatCost(cost: number | 'X' | 'unplayable'): string {
  if (cost === 'unplayable') return '—';
  return String(cost);
}

interface Props {
  item: Item;
  relationshipCount: number;
}

export default function ItemCard({ item, relationshipCount }: Props) {
  const typeLabel = item.itemType === 'card' ? item.type : 'relic';
  const typeStyle = CARD_TYPE_STYLES[typeLabel] ?? CARD_TYPE_STYLES.relic;
  const rarityStyle = RARITY_STYLES[item.rarity] ?? 'text-gray-300';

  return (
    <Link
      href={itemUrl(item)}
      className="block rounded-lg border border-slate-700 bg-slate-800 p-3 transition-colors hover:border-slate-500 hover:bg-slate-750"
    >
      <WikiImage
        item={item}
        width={240}
        height={112}
        className="w-full h-28 object-contain mb-2 rounded"
      />

      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-slate-100 text-sm leading-snug">{item.name}</span>
        {item.itemType === 'card' && (
          <span className="shrink-0 text-xs font-bold text-slate-400 mt-0.5">
            {formatCost(item.cost)}
          </span>
        )}
      </div>

      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <span className={`text-xs px-1.5 py-0.5 rounded border ${typeStyle}`}>
          {typeLabel}
        </span>
        <span className={`text-xs capitalize ${rarityStyle}`}>{item.rarity}</span>
        {item.itemType === 'relic' && (
          <span className="text-xs text-slate-500 capitalize">{item.character}</span>
        )}
      </div>

      <p className="mt-2 text-xs text-slate-400 line-clamp-2 leading-relaxed">
        {item.description}
      </p>

      {relationshipCount > 0 && (
        <p className="mt-2 text-xs text-violet-400">
          {relationshipCount} relationship{relationshipCount !== 1 ? 's' : ''}
        </p>
      )}
    </Link>
  );
}
